import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { OrderFlowLogo } from "@/components/OrderFlowLogo";
import { useLanguage } from "@/contexts/LanguageContext";
import { Eye, EyeOff } from "lucide-react";
import { loginSchema, passwordResetSchema } from "@/lib/validationSchemas";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  redirectPath?: string;
  onSwitchToRegister?: () => void;
}

export function AuthModal({ 
  open, 
  onOpenChange, 
  onSuccess,
  redirectPath = "/orders",
  onSwitchToRegister
}: AuthModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const { t, language } = useLanguage();

  // Pre-fill email from URL params (when redirected from /explore)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefillEmail = params.get("email");
    if (prefillEmail) {
      setEmail(decodeURIComponent(prefillEmail));
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    
    // Check for errors in URL
    if (hash.includes('error_description=') || hash.includes('error_code=')) {
      const params = new URLSearchParams(hash.substring(1));
      const errorDescription = params.get('error_description');
      
      toast({
        title: t("auth.error"),
        description: errorDescription || t("auth.authenticationError"),
        variant: "destructive",
      });
      
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [toast, t]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // If no password entered, auto-trigger password reset (magic link)
      if (!password.trim()) {
        // Validate email at least
        passwordResetSchema.parse({ email });
        
        await supabase.functions.invoke("send-password-reset-email", {
          body: { email, language },
        });
        
        toast({
          title: t("auth.magicLinkSent"),
          description: t("auth.checkEmailForLink"),
          duration: 6000,
        });
        setLoading(false);
        return;
      }

      // Validate input
      loginSchema.parse({ email, password });

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Check if user has a pending or rejected application
        const { data: application } = await supabase
          .from('user_applications')
          .select('status')
          .eq('email', email.toLowerCase())
          .single();

        if (application) {
          if (application.status === 'pending') {
            throw new Error(
              language === 'ka'
                ? 'თქვენი განაცხადი განხილვის პროცესშია. გთხოვთ დაელოდოთ ადმინისტრატორის დადასტურებას.'
                : 'Your application is pending review. Please wait for administrator approval.'
            );
          } else if (application.status === 'rejected') {
            throw new Error(
              language === 'ka'
                ? 'თქვენი განაცხადი არ დამტკიცდა. დამატებითი ინფორმაციისთვის დაგვიკავშირდით.'
                : 'Your application was not approved. Please contact us for more information.'
            );
          }
        }

        // For invalid credentials, suggest forgot password
        if (error.message?.includes("Invalid login credentials")) {
          toast({
            title: t("auth.error"),
            description: t("auth.tryForgotPassword"),
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        throw error;
      }

      // Success - close modal
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t("auth.error"),
        description: error.message || "Invalid credentials",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate email
      passwordResetSchema.parse({ email });
      
      console.log(`🔐 [AuthModal] Requesting password reset for: ${email}`);
      
      const { error } = await supabase.functions.invoke("send-password-reset-email", {
        body: { 
          email,
          language 
        },
      });

      if (error) {
        console.error('❌ [AuthModal] Password reset error:', error);
        throw error;
      }

      console.log('✅ [AuthModal] Password reset email sent successfully');

      toast({
        title: t("auth.success"),
        description: t("auth.passwordResetEmailSent"),
        duration: 6000,
      });

      setIsResetMode(false);
      setEmail("");
    } catch (error: any) {
      console.error('❌ [AuthModal] Failed to send password reset:', error);
      toast({
        title: t("auth.error"),
        description: error.message || t("auth.passwordResetFailed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-[95vw] sm:max-w-[425px] overflow-x-hidden min-w-0">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <OrderFlowLogo className="h-12 w-auto" />
          </div>
          <DialogTitle className="text-center text-2xl">
            {isResetMode
              ? t("auth.resetPassword")
              : t("auth.welcomeBack")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {isResetMode
              ? t("auth.resetPasswordPrompt")
              : t("auth.signInPrompt")}
          </DialogDescription>
        </DialogHeader>

        {isResetMode ? (
          <form onSubmit={handlePasswordReset} className="space-y-4 min-w-0 overflow-hidden">
            <div className="space-y-2">
              <Label htmlFor="reset-email">{t("auth.email")}</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder={t("auth.placeholders.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoFocus
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("auth.sending") : t("auth.sendResetLink")}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setIsResetMode(false)}
              disabled={loading}
            >
              {t("auth.backToSignIn")}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleAuth} className="space-y-4 min-w-0 overflow-hidden">
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("auth.placeholders.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <button
                  type="button"
                  onClick={() => setIsResetMode(true)}
                  className="text-sm text-primary hover:underline"
                  disabled={loading}
                >
                  {t("auth.forgotPassword")}
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("auth.placeholders.password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("auth.signingIn") : t("auth.signIn")}
            </Button>

            {onSwitchToRegister && (
              <div className="text-center text-sm">
                <span className="text-muted-foreground">{t("auth.noAccount")}</span>{" "}
                <button
                  type="button"
                  onClick={onSwitchToRegister}
                  className="text-primary hover:underline font-medium"
                  disabled={loading}
                >
                  {t("auth.signUp")}
                </button>
              </div>
            )}
          </form>
        )}

      </DialogContent>
    </Dialog>
  );
}
